import { useEffect } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator,
} from "@/components/ui/command";
import {
  Wallet, Settings, LogOut, Play, Cpu, Boxes,
} from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { PRODUCT_HOME } from "@/lib/product";
import { isWalletCloudMode } from "@/lib/runtime-mode";

const NAV = [
  { to: PRODUCT_HOME, label: "Wallet", icon: Wallet },
  { to: "/run-workloads", label: "Run Workloads", icon: Play },
  { to: "/crm", label: "CRM", icon: Cpu },
  { to: "/store", label: "Store", icon: Boxes },
] as const;

export function CommandPalette({
  open, onOpenChange,
}: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const navigate = useNavigate();
  const { logout } = useAuth();

  // global ⌘K / Ctrl+K
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  const go = (to: string) => {
    onOpenChange(false);
    navigate({ to });
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Search wallet, workloads, CRM, store…" />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>

        <CommandGroup heading="Navigation">
          {NAV.map((n) => {
            const Icon = n.icon;
            return (
              <CommandItem key={n.to} value={`nav ${n.label}`} onSelect={() => go(n.to)}>
                <Icon className="mr-2 h-4 w-4" /> {n.label}
              </CommandItem>
            );
          })}
        </CommandGroup>

        <CommandSeparator />
        <CommandGroup heading="Quick actions">
          <CommandItem value="action buy genrl credits store" onSelect={() => go("/store")}>
            <Boxes className="mr-2 h-4 w-4" /> Buy GENRL credits
          </CommandItem>
          <CommandItem value="action launch workload run compute" onSelect={() => go("/run-workloads")}>
            <Play className="mr-2 h-4 w-4" /> Launch workload
          </CommandItem>
          <CommandItem value="action settings" onSelect={() => go("/settings")}>
            <Settings className="mr-2 h-4 w-4" /> Open settings
          </CommandItem>
          <CommandItem
            value="action logout sign out"
            onSelect={async () => {
              onOpenChange(false);
              await logout();
              if (!isWalletCloudMode()) {
                navigate({ to: "/login" });
              }
            }}
          >
            <LogOut className="mr-2 h-4 w-4" /> Sign out
          </CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
